/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\AdvancedTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/

import { Megaphone, Phone } from 'lucide-react';
import { CodecField } from '../../../components/ui/CodecField';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { useI18n } from '../../../hooks/useI18n';

interface AdvancedTabProps {
  formData: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    settings: Record<string, any>;
  };
  mohSources: Array<{ id: number; name: string; slug?: string }>;
  handleSettingChange: (key: string, value: string | boolean | number | null | Array<unknown>) => void;
  onSubmit: (event?: React.FormEvent) => void;
}

export function AdvancedTab({ formData, mohSources, handleSettingChange, onSubmit }: AdvancedTabProps) {
  const { t } = useI18n();

  return (
    <form onSubmit={onSubmit}>
      {/* Audio & Media */}
      <FormBox title={t('pagingGroups.sections.audioAndMedia')} icon={<Megaphone className="h-5 w-5" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          <Label htmlFor="codec">
            <SimpleTooltip content={t('pagingGroups.fields.codec.tooltip')}>{t('pagingGroups.fields.codec.label')}</SimpleTooltip>
          </Label>
          <div>
            <CodecField value={formData.settings?.codec || 'PCMU'} onChange={(value) => handleSettingChange('codec', value)}
              placeholder={t('pagingGroups.fields.codec.placeholder')} allowVideo={false} />
          </div>

          <Label htmlFor="volume">
            <SimpleTooltip content={t('pagingGroups.fields.volume.tooltip')}>{t('pagingGroups.fields.volume.label')}</SimpleTooltip>
          </Label>
          <div>
            <Input id="volume" type="number" min="-5" max="5" value={formData.settings?.volume ?? 5}
              onChange={(e) => handleSettingChange('volume', parseInt(e.target.value) || 0)}
              placeholder={t('pagingGroups.fields.volume.placeholder')} />
          </div>

          <Label htmlFor="announcementPath">
            <SimpleTooltip content={t('pagingGroups.fields.announcementPath.tooltip')}>{t('pagingGroups.fields.announcementPath.label')}</SimpleTooltip>
          </Label>
          <div>
            <RecordingSelect value={formData.settings?.announcementPath ?? ''}
              onChange={(value) => handleSettingChange('announcementPath', value === '' ? null : value)}
              placeholder={t('pagingGroups.fields.announcementPath.placeholder')} returnType="id" showPlayButton={true} />
          </div>

          <Label htmlFor="settings.holdMusic">
            <SimpleTooltip content={t('pagingGroups.fields.holdMusic.tooltip')}>{t('pagingGroups.fields.holdMusic.label')}</SimpleTooltip>
          </Label>
          <div>
            <Select value={formData.settings?.holdMusic || 'local_stream://default'} onValueChange={(value) => handleSettingChange('holdMusic', value)}>
              <SelectTrigger><SelectValue placeholder={t('pagingGroups.fields.holdMusic.placeholder')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local_stream://default">{t('pagingGroups.fields.holdMusic.default')}</SelectItem>
                {mohSources.map((moh) => <SelectItem key={moh.id} value={moh.name}>{moh.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Label htmlFor="settings.energyLevel">
            <SimpleTooltip content={t('pagingGroups.fields.energyLevel.tooltip')}>{t('pagingGroups.fields.energyLevel.label')}</SimpleTooltip>
          </Label>
          <div>
            <Input id="settings.energyLevel" type="number" min="100" max="1000"
              value={formData.settings?.energyLevel || 300}
              onChange={(e) => handleSettingChange('energyLevel', parseInt(e.target.value) || 300)}
              placeholder={t('pagingGroups.fields.energyLevel.placeholder')} />
          </div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.comfortNoise.tooltip')}>{t('pagingGroups.fields.comfortNoise.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.comfortNoise ?? true} onChange={(checked) => handleSettingChange('comfortNoise', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.autoGain.tooltip')}>{t('pagingGroups.fields.autoGain.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.autoGain ?? false} onChange={(checked) => handleSettingChange('autoGain', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.suppressDtmf.tooltip')}>{t('pagingGroups.fields.suppressDtmf.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.suppressDtmf ?? false} onChange={(checked) => handleSettingChange('suppressDtmf', checked)} /></div>
        </div>
      </FormBox>

      {/* Session Behavior */}
      <FormBox title={t('pagingGroups.sections.sessionBehavior')} icon={<Phone className="h-5 w-5" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          <Label><SimpleTooltip content={t('pagingGroups.fields.recordEnabled.tooltip')}>{t('pagingGroups.fields.recordEnabled.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.recordEnabled ?? false} onChange={(checked) => handleSettingChange('recordEnabled', checked)} /></div>

          {formData.settings?.recordEnabled && (
            <>
              <Label htmlFor="settings.recordPath">
                <SimpleTooltip content={t('pagingGroups.fields.recordPath.tooltip')}>{t('pagingGroups.fields.recordPath.label')}</SimpleTooltip>
              </Label>
              <div>
                <Input id="settings.recordPath" type="text"
                  value={formData.settings?.recordPath || '/usr/share/freeswitch/recordings/paging'}
                  onChange={(e) => handleSettingChange('recordPath', e.target.value)}
                  placeholder={t('pagingGroups.fields.recordPath.placeholder')} />
              </div>
            </>
          )}

          <Label><SimpleTooltip content={t('pagingGroups.fields.muteParticipants.tooltip')}>{t('pagingGroups.fields.muteParticipants.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.muteParticipants ?? false} onChange={(checked) => handleSettingChange('muteParticipants', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.intercomMode.tooltip')}>{t('pagingGroups.fields.intercomMode.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.intercomMode ?? false} onChange={(checked) => handleSettingChange('intercomMode', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.announceJoinLeave.tooltip')}>{t('pagingGroups.fields.announceJoinLeave.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.announceJoinLeave ?? false} onChange={(checked) => handleSettingChange('announceJoinLeave', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.allowExternalCalls.tooltip')}>{t('pagingGroups.fields.allowExternalCalls.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.allowExternalCalls ?? false} onChange={(checked) => handleSettingChange('allowExternalCalls', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.autoAnswer.tooltip')}>{t('pagingGroups.fields.autoAnswer.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.autoAnswer ?? false} onChange={(checked) => handleSettingChange('autoAnswer', checked)} /></div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.skipBusy.tooltip')}>{t('pagingGroups.fields.skipBusy.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.skipBusy ?? false} onChange={(checked) => handleSettingChange('skipBusy', checked)} /></div>

          <Label htmlFor="settings.maxParticipants">
            <SimpleTooltip content={t('pagingGroups.fields.maxParticipants.tooltip')}>{t('pagingGroups.fields.maxParticipants.label')}</SimpleTooltip>
          </Label>
          <div>
            <Input id="settings.maxParticipants" type="number" min="1" max="500"
              value={formData.settings?.maxParticipants || 50}
              onChange={(e) => handleSettingChange('maxParticipants', parseInt(e.target.value) || 50)}
              placeholder={t('pagingGroups.fields.maxParticipants.placeholder')} />
          </div>

          <Label><SimpleTooltip content={t('pagingGroups.fields.pageDelayStart.tooltip')}>{t('pagingGroups.fields.pageDelayStart.label')}</SimpleTooltip></Label>
          <div><Toggle value={formData.settings?.pageDelayStart ?? false} onChange={(checked) => handleSettingChange('pageDelayStart', checked)} /></div>

          <Label htmlFor="settings.pinCode">
            <SimpleTooltip content={t('pagingGroups.fields.pinCode.tooltip')}>{t('pagingGroups.fields.pinCode.label')}</SimpleTooltip>
          </Label>
          <div>
            <Input id="settings.pinCode" type="text" value={formData.settings?.pinCode || ''}
              onChange={(e) => handleSettingChange('pinCode', e.target.value)}
              placeholder={t('pagingGroups.fields.pinCode.placeholder')} />
            {formData.settings?.pinCode && formData.settings.pinCode.trim() !== '' && (
              <p className="text-xs text-muted-foreground mt-1">{t('pagingGroups.fields.pinCode.hint')}</p>
            )}
          </div>
        </div>
      </FormBox>
    </form>
  );
}
